/**
 * Reusable Zod schemas shared across backend and frontend.
 *
 * Anything that's a generic field-level validator — not tied to a specific
 * resource or domain — belongs here. Domain-specific schemas (Order, Place,
 * etc.) stay in their own modules.
 */

import * as z from "zod";

/**
 * Zod schema for a list-shaped URL parameter. Mirrors the query parser's
 * output: the canonical wire shape is a comma string (`includes=a,b,c`),
 * the parser flattens indexed-bracket arrays to the same shape, and this
 * helper splits back into `string[]` after validation.
 *
 * Output type is `string[] | "" | undefined` — `undefined` when the field is
 * absent from the URL, `""` when set to an empty placeholder (filter shell
 * with no values yet), `[]` when present but blank after parsing, otherwise a
 * non-empty array. Pre-split arrays are accepted too so legacy bookmarked
 * URLs still validate.
 *
 * Example:
 * ```ts
 * const schema = z.object({
 *   filters: z.object({ statusIds: commaListSchema }).optional(),
 * });
 * ```
 */
export const commaListSchema = z
  .union([z.string(), z.array(z.string()), z.null()])
  .transform((val): string[] | "" => {
    if (val === "") return "";
    if (val == null) return [];
    if (Array.isArray(val)) {
      if (val.length === 1 && val[0] === "") return "";
      return val.filter(Boolean);
    }
    if (val.trim() === "") return "";
    return val
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  })
  .optional();

/**
 * Collapses a {@link commaListSchema} output to "a non-empty list, or nothing":
 * the empty placeholder `""`, an absent field and a blank `[]` all become
 * `undefined`, so an unfilled filter chip never narrows a query.
 *
 * Example:
 * ```ts
 * const labels = toNonEmptyList(query.labels) ?? [];
 * ```
 */
export function toNonEmptyList(
  value: string[] | "" | undefined,
): string[] | undefined {
  return Array.isArray(value) && value.length > 0 ? value : undefined;
}

/**
 * Zod schema for a nested date-range filter in URL query state. Mirrors the
 * wire shape used by filter UI components: `{ start?, end?, label? }` with
 * optional ISO date strings and a preset label (e.g. "Today", "Last 7 days").
 *
 * Output type is `{ start?: string; end?: string; label?: string } | "" |
 * undefined` — `undefined` when the field is absent from the URL, `""` when
 * set to an empty placeholder (filter shell with no values yet), otherwise the
 * parsed object.
 *
 * Example:
 * ```ts
 * const schema = z.object({
 *   filters: z.object({ dateRange: filterDateRangeSchema }).optional(),
 * });
 * ```
 */
export const filterDateRangeSchema = z
  .object({
    start: z.string().optional(),
    end: z.string().optional(),
    label: z.string().optional(),
  })
  .optional()
  .or(z.literal(""));

/**
 * Zod schema for an email form field. Trims, validates format, lowercases,
 * and surfaces only one error at a time (required vs. invalid format) so
 * forms don't show two messages for an empty input.
 *
 * Use everywhere a user-typed email is captured — sign-in, sign-up, user
 * forms, ingest payloads. Output is the trimmed, lowercased string.
 */
export const emailFieldSchema = z
  .string()
  .trim()
  .superRefine((val, ctx) => {
    if (!val) {
      ctx.addIssue({
        code: "custom",
        message: "Please enter your email address.",
      });
    } else if (!z.email().safeParse(val).success) {
      ctx.addIssue({
        code: "custom",
        message: "Please enter a valid email address.",
      });
    }
  })
  .transform((val) => val.toLowerCase());

/** Zero-width and non-breaking characters that ride along with a pasted email. */
const INVISIBLE_CHARS_REGEX = /[\u200B-\u200D\uFEFF\u00A0\u2060]/g;

/**
 * Zod schema for an email in an API request body: strips invisible characters,
 * trims and lowercases, then validates the format. Output is the normalized
 * address. Unlike {@link emailFieldSchema} (a form field with its own
 * required/invalid copy), the caller owns the single invalid-format message.
 *
 * Example:
 * ```ts
 * const schema = z.object({
 *   email: normalizedEmailSchema(),
 *   adminEmail: normalizedEmailSchema("Admin email must be a valid email"),
 * });
 * ```
 *
 * @param message - Error for an invalid address (default "Invalid email format").
 */
export function normalizedEmailSchema(message = "Invalid email format") {
  return z
    .string()
    .transform((val) =>
      val.replace(INVISIBLE_CHARS_REGEX, "").trim().toLowerCase(),
    )
    .pipe(z.email(message));
}

/**
 * Whether a string names a time zone this runtime can resolve — `Intl`
 * throws `RangeError` on an unknown zone, which is the only portable check.
 */
function isResolvableTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Zod schema for an IANA time zone name (`America/New_York`, `UTC`). Trims
 * and rejects anything the runtime cannot resolve, so a caller can format
 * dates in it without a second check. Output is the trimmed name.
 */
export const ianaTimeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .refine(isResolvableTimeZone, { message: "Unknown IANA time zone" });
